import React, { useEffect, useMemo, useState } from 'react';
import {
  LayoutGrid, List, Pencil, Plus, Trash2, ChevronLeft, Save, Loader2,
  GripVertical, RotateCcw, ExternalLink,
} from 'lucide-react';
import { adminApi } from './api';

const VIEW_TILES = 'tiles';
const VIEW_LIST = 'list';

// ===========================================================================
// Утилиты для превью блока
// ===========================================================================
function summarize(meta, data) {
  if (!data) return '';
  if (meta.id === 'hero') return data.title || '';
  if (meta.id === 'manifest') return `«${data.quote || ''}»`;
  if (meta.id === 'about') return (data.paragraphs?.[0] || '').slice(0, 110) + '…';
  if (meta.id === 'contact') return `${data.phone || ''} · ${data.email || ''}`;
  if (Array.isArray(data.items)) {
    return data.items.slice(0, 3).map((it) => it.name || it.author || `${it.price}${data.currency || ''}`).join(' · ');
  }
  return JSON.stringify(data).slice(0, 100);
}

function itemCount(meta, data) {
  if (meta.id === 'about') return data.paragraphs?.length || 0;
  if (Array.isArray(data?.items)) return data.items.length;
  return 0;
}

// ===========================================================================
// Карточка-плитка
// ===========================================================================
function BlockTile({ meta, data, onClick }) {
  const count = itemCount(meta, data);
  return (
    <button
      onClick={onClick}
      className="dark-card hoverable text-left"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 168 }}
      data-testid={`block-tile-${meta.id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="dark-eyebrow" style={{ fontSize: 10 }}>Блок</div>
          <div className="dark-h2" style={{ fontSize: 18, marginTop: 4 }}>{meta.name}</div>
        </div>
        <Pencil size={14} style={{ color: 'var(--gold)', opacity: 0.6, flexShrink: 0 }} strokeWidth={1.7} />
      </div>
      <p style={{ fontSize: 13, color: 'var(--cream-mute)', lineHeight: 1.5, flex: 1 }}>
        {summarize(meta, data)}
      </p>
      <div className="flex items-center justify-between" style={{ marginTop: 'auto' }}>
        {count > 0 ? (
          <span className="dark-tag">{count} {count === 1 ? 'элемент' : count < 5 ? 'элемента' : 'элементов'}</span>
        ) : <span />}
        <span style={{ fontSize: 11, color: 'var(--gold)' }}>Открыть →</span>
      </div>
    </button>
  );
}

// ===========================================================================
// Строка-список
// ===========================================================================
function BlockRow({ meta, data, onClick }) {
  const count = itemCount(meta, data);
  return (
    <button
      onClick={onClick}
      className="dark-card hoverable text-left"
      style={{
        padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16, width: '100%',
      }}
      data-testid={`block-row-${meta.id}`}
    >
      <div style={{ flexShrink: 0, width: 100 }}>
        <div className="dark-h2" style={{ fontSize: 16 }}>{meta.name}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: 13, color: 'var(--cream-mute)', lineHeight: 1.5,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {summarize(meta, data)}
        </p>
      </div>
      {count > 0 && (
        <span className="dark-tag" style={{ flexShrink: 0 }}>{count}</span>
      )}
      <Pencil size={14} style={{ color: 'var(--gold)', opacity: 0.6, flexShrink: 0 }} strokeWidth={1.7} />
    </button>
  );
}

// ===========================================================================
// Редактор блока (правая панель)
// ===========================================================================

const FIELD_LABELS = {
  title: 'Заголовок',
  subtitle: 'Подзаголовок',
  lead: 'Основной текст',
  lead_2: 'Второй абзац',
  tagline: 'Подпись внизу',
  cta_primary: 'Главная кнопка',
  cta_secondary: 'Вторая кнопка',
  quote: 'Цитата',
  author: 'Автор / имя',
  intro: 'Вводный текст',
  name: 'Название',
  desc: 'Описание',
  price: 'Цена',
  duration: 'Длительность',
  note: 'Подпись',
  text: 'Текст отзыва',
  currency: 'Валюта',
  phone: 'Телефон',
  telegram: 'Telegram',
  email: 'Email',
  address: 'Адрес',
};

function FieldRow({ name, value, onChange, multiline, testid }) {
  const label = FIELD_LABELS[name] || name;
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: 11, color: 'var(--cream-soft)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
        {label}
      </span>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="dark-input"
          rows={3}
          style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }}
          data-testid={testid}
        />
      ) : (
        <input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="dark-input"
          data-testid={testid}
        />
      )}
    </label>
  );
}

// Карта block.id → id секции на публичном сайте (для якорной прокрутки).
const PUBLIC_ANCHOR = {
  hero: 'top',
  manifest: 'manifest',
  about: 'about',
  services: 'services',
  how: 'how',
  reviews: 'reviews',
  pricing: 'pricing',
  contact: 'contact',
};

function openPublicPreview(blockId) {
  const anchor = PUBLIC_ANCHOR[blockId] || '';
  // Cache-buster, чтобы Светлана после сохранения сразу увидела свежий контент.
  const url = `/?preview=${Date.now()}${anchor ? '#' + anchor : ''}`;
  window.open(url, '_blank', 'noopener');
}

function BlockEditor({ meta, initialData, onClose, onSaved }) {
  const [data, setData] = useState(() => JSON.parse(JSON.stringify(initialData)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedHint, setSavedHint] = useState(false);

  // Простые поля
  const simpleFields = useMemo(() => {
    const skip = new Set(['paragraphs', 'items']);
    return Object.keys(data).filter((k) => !skip.has(k));
  }, [data]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const updateParagraph = (i, v) => {
    setData((d) => {
      const p = [...(d.paragraphs || [])];
      p[i] = v;
      return { ...d, paragraphs: p };
    });
  };
  const addParagraph = () => setData((d) => ({ ...d, paragraphs: [...(d.paragraphs || []), ''] }));
  const removeParagraph = (i) => setData((d) => ({ ...d, paragraphs: (d.paragraphs || []).filter((_, j) => j !== i) }));

  const updateItem = (i, key, v) => {
    setData((d) => {
      const items = [...(d.items || [])];
      items[i] = { ...items[i], [key]: v };
      return { ...d, items };
    });
  };
  const addItem = () => {
    const blank = {};
    (meta.item_fields || []).forEach((f) => { blank[f] = ''; });
    blank.id = 'new_' + Math.random().toString(36).slice(2, 8);
    setData((d) => ({ ...d, items: [...(d.items || []), blank] }));
  };
  const removeItem = (i) => setData((d) => ({ ...d, items: (d.items || []).filter((_, j) => j !== i) }));

  const onSave = async () => {
    setSaving(true);
    setError('');
    try {
      await adminApi.contentUpdate(meta.id, data);
      setSavedHint(true);
      setTimeout(() => setSavedHint(false), 2200);
      onSaved && onSaved(data);
    } catch (err) {
      setError(err.message || 'Не получилось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => setData(JSON.parse(JSON.stringify(initialData)));

  return (
    <div className="dark-card" style={{ padding: 24, marginTop: 16 }} data-testid={`block-editor-${meta.id}`}>
      <div className="flex items-center justify-between" style={{ marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="dark-btn dark-btn-ghost" style={{ padding: '8px 12px' }} data-testid="editor-back">
            <ChevronLeft size={14} /> К списку
          </button>
          <div>
            <div className="dark-eyebrow" style={{ fontSize: 10 }}>Редактор блока</div>
            <h2 className="dark-h2" style={{ fontSize: 22, marginTop: 2 }}>{meta.name}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onReset} className="dark-btn dark-btn-ghost" style={{ padding: '8px 12px', fontSize: 12 }} data-testid="editor-reset">
            <RotateCcw size={12} /> Сбросить
          </button>
          <button onClick={() => openPublicPreview(meta.id)} className="dark-btn dark-btn-ghost" style={{ padding: '8px 12px', fontSize: 12 }} data-testid="editor-preview" title="Открыть публичный сайт в новой вкладке с прокруткой к этому блоку">
            <ExternalLink size={12} /> Предпросмотр на сайте
          </button>
          <button onClick={onSave} disabled={saving} className="dark-btn dark-btn-primary" style={{ padding: '10px 16px' }} data-testid="editor-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ color: 'var(--wine-bright)', fontSize: 13, marginBottom: 12 }} data-testid="editor-error">{error}</div>
      )}
      {savedHint && (
        <div style={{ color: 'var(--gold)', fontSize: 13, marginBottom: 12 }} data-testid="editor-saved">
          Сохранено. Откройте «Предпросмотр на сайте» — изменения уже там.
        </div>
      )}

      {/* Простые поля */}
      <div>
        {simpleFields.map((k) => (
          <FieldRow
            key={k}
            name={k}
            value={data[k]}
            onChange={(v) => setField(k, v)}
            multiline={k === 'lead' || k === 'lead_2' || k === 'quote' || k === 'desc' || k === 'text'}
            testid={`field-${meta.id}-${k}`}
          />
        ))}
      </div>

      {/* Список абзацев (about) */}
      {meta.kind === 'fields_with_list' && (
        <div style={{ marginTop: 8 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
            <h3 className="dark-h2" style={{ fontSize: 16 }}>{meta.list_item_label || 'Элемент'}ы</h3>
            <button onClick={addParagraph} className="dark-btn dark-btn-gold" style={{ padding: '6px 12px', fontSize: 12 }} data-testid="paragraph-add">
              <Plus size={12} /> Добавить
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(data.paragraphs || []).map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }} data-testid={`paragraph-${i}`}>
                <GripVertical size={14} style={{ color: 'var(--cream-soft)', marginTop: 14, flexShrink: 0 }} />
                <textarea
                  value={p}
                  onChange={(e) => updateParagraph(i, e.target.value)}
                  className="dark-input"
                  rows={3}
                  style={{ resize: 'vertical', flex: 1 }}
                  data-testid={`paragraph-input-${i}`}
                />
                <button
                  onClick={() => removeParagraph(i)}
                  className="dark-btn dark-btn-ghost"
                  style={{ padding: 10, flexShrink: 0 }}
                  data-testid={`paragraph-del-${i}`}
                  title="Удалить абзац"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Список карточек-айтемов (services, how, reviews, pricing) */}
      {meta.kind === 'fields_with_items' && (
        <div style={{ marginTop: 8 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
            <h3 className="dark-h2" style={{ fontSize: 16 }}>Элементы</h3>
            <button onClick={addItem} className="dark-btn dark-btn-gold" style={{ padding: '6px 12px', fontSize: 12 }} data-testid="item-add">
              <Plus size={12} /> Добавить
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {(data.items || []).map((it, i) => (
              <div
                key={it.id || i}
                className="dark-card"
                style={{ padding: 14, background: 'rgba(255,255,255,0.02)' }}
                data-testid={`item-${meta.id}-${i}`}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
                  <span className="dark-eyebrow" style={{ fontSize: 10 }}>№ {i + 1}</span>
                  <button
                    onClick={() => removeItem(i)}
                    className="dark-btn dark-btn-ghost"
                    style={{ padding: '6px 10px', fontSize: 11 }}
                    data-testid={`item-del-${meta.id}-${i}`}
                  >
                    <Trash2 size={12} /> Удалить
                  </button>
                </div>
                {(meta.item_fields || []).map((f) => (
                  <FieldRow
                    key={f}
                    name={f}
                    value={it[f]}
                    onChange={(v) => updateItem(i, f, v)}
                    multiline={f === 'desc' || f === 'text'}
                    testid={`item-${meta.id}-${i}-${f}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Главный компонент раздела
// ===========================================================================
export default function AdminContent() {
  const [view, setView] = useState(VIEW_TILES);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const d = await adminApi.contentAll();
      setData(d);
      setError('');
    } catch (err) {
      setError(err.message || 'Не получилось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: '120px 0' }} data-testid="content-loading">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--cream-soft)' }} />
      </div>
    );
  }

  const blocks = data?.blocks || [];
  const content = data?.content || {};
  const editingBlock = blocks.find((b) => b.id === editingId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} data-testid="admin-content">
      {/* Шапка раздела + переключатель режимов */}
      <div className="dark-card" style={{ padding: 28 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="dark-eyebrow">Управление</div>
            <h1 className="dark-h1" style={{ fontSize: 42, marginTop: 6 }}>Содержимое сайта</h1>
            <p className="dark-meta" style={{ marginTop: 10, maxWidth: 580, lineHeight: 1.6 }}>
              Все 8 блоков сайта. Откройте любой — измените тексты, добавьте отзыв, поправьте цену.
              Изменения сразу видны на публичном сайте — кнопка «Предпросмотр» в редакторе блока.
            </p>
          </div>

          <div
            style={{
              display: 'inline-flex', padding: 4,
              background: 'rgba(255,255,255,0.03)', border: '1px solid var(--ink-line)',
              borderRadius: 12,
            }}
            data-testid="view-toggle"
          >
            <button
              onClick={() => setView(VIEW_TILES)}
              className={`dark-btn ${view === VIEW_TILES ? 'dark-btn-gold' : 'dark-btn-ghost'}`}
              style={{ border: 'none', padding: '8px 12px', fontSize: 12 }}
              data-testid="view-tiles"
            >
              <LayoutGrid size={14} /> Плитки
            </button>
            <button
              onClick={() => setView(VIEW_LIST)}
              className={`dark-btn ${view === VIEW_LIST ? 'dark-btn-gold' : 'dark-btn-ghost'}`}
              style={{ border: 'none', padding: '8px 12px', fontSize: 12 }}
              data-testid="view-list"
            >
              <List size={14} /> Список
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="dark-card" style={{ padding: 16, color: 'var(--wine-bright)' }} data-testid="content-error">{error}</div>
      )}

      {/* Список блоков или плитки */}
      {!editingId && view === VIEW_TILES && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 14,
          }}
          data-testid="blocks-tiles"
        >
          {blocks.map((b) => (
            <BlockTile key={b.id} meta={b} data={content[b.id]} onClick={() => setEditingId(b.id)} />
          ))}
        </div>
      )}

      {!editingId && view === VIEW_LIST && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="blocks-list">
          {blocks.map((b) => (
            <BlockRow key={b.id} meta={b} data={content[b.id]} onClick={() => setEditingId(b.id)} />
          ))}
        </div>
      )}

      {/* Редактор */}
      {editingBlock && (
        <BlockEditor
          meta={editingBlock}
          initialData={content[editingBlock.id]}
          onClose={() => setEditingId(null)}
          onSaved={(newData) => setData((d) => ({ ...d, content: { ...d.content, [editingBlock.id]: newData } }))}
        />
      )}
    </div>
  );
}
